import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiErrorText,
  ButtonSmall,
  CopyButton,
  Loader,
  OpenInNewTabButton,
} from '@/shared/components';
import { useAssetsIcons, useOperationTypesIcons } from '@/shared/api';
import type { OperationDetailsResponse, OperationDto } from '@/shared/types';
import { cn } from '@/shared/utils';
import CloseIcon from '@/shared/assets/close-icon.svg?react';
import Close16Icon from '@/shared/assets/close-16-icon.svg?react';
import DownloadIcon from '@/shared/assets/download-16-icon.svg?react';
import { useOperationDetailsState } from '../model/useOperationDetailsState';
import { OperationDetailsHeader } from './OperationDetailsHeader';
import { OperationProcessingTimeHint } from './OperationProcessingTimeHint';

type DetailsParam = OperationDetailsResponse['blocks'][number]['block'][number];

type Props = {
  operation: OperationDto;
  onClose: () => void;
  onStatusChange?: () => void;
  className?: string;
};

const DATE_PARAM_IDS = ['pCreatedDateTime', 'pExecutedDateTime'];
const PROCESSING_STATUSES = ['pending', 'failed'];
const TITLE_DUPLICATE_PARAM_IDS = ['pTransactionType', 'pSubType'];

const getDisplayTitle = (details: OperationDetailsResponse) => {
  const title = details.operationDisplay?.title;

  return title?.trim() ? title : undefined;
};

const isParamVisible = (
  param: DetailsParam,
  details: OperationDetailsResponse
) => {
  if (param.paramId === 'pCurrentTrStatus') return false;

  if (getDisplayTitle(details)) {
    return !TITLE_DUPLICATE_PARAM_IDS.includes(param.paramId);
  }

  return !(
    param.paramId === 'pTransactionType' &&
    param.paramValue === details.operationType
  );
};

const getVisibleBlocks = (details: OperationDetailsResponse) =>
  (details.blocks ?? [])
    .map(({ block }) => block.filter(param => isParamVisible(param, details)))
    .filter(params => params.length > 0);

const ParamItem = ({
  name,
  children,
  isWide = false,
}: {
  name: string;
  children: ReactNode;
  isWide?: boolean;
}) => (
  <div
    className={cn(
      'flex min-w-0 flex-col',
      isWide ? 'gap-[0.2rem]' : 'gap-[0.4rem]'
    )}>
    <p className="p6 text-secondary">{name}</p>
    {children}
  </div>
);

const ParamValue = ({ param }: { param: DetailsParam }) => (
  <div className="flex items-start justify-between gap-[2.4rem]">
    <p className="medium5 text-primary wrap-anywhere">{param.paramValue}</p>

    {(param.paramCopy || param.paramOpen) && (
      <div className="flex shrink-0 items-center gap-[0.8rem]">
        {param.paramCopy && (
          <CopyButton
            value={param.paramValue}
            size="md"
            variant="secondary"
            buttonClassName="p-0"
          />
        )}
        {param.paramOpen && (
          <OpenInNewTabButton
            url={param.paramValue}
            size="md"
            variant="secondary"
            buttonClassName="p-0"
          />
        )}
      </div>
    )}
  </div>
);

const ParamsGroup = ({ params }: { params: DetailsParam[] }) => {
  const dateParams = params.filter(param =>
    DATE_PARAM_IDS.includes(param.paramId)
  );
  const firstDateParam = dateParams[0];

  return (
    <>
      {params.map((param, index) => {
        if (DATE_PARAM_IDS.includes(param.paramId)) {
          if (param !== firstDateParam) return null;

          return (
            <div
              key={param.paramId}
              className={cn(
                'grid items-start gap-[1.6rem]',
                dateParams.length > 1 && 'grid-cols-2'
              )}>
              {dateParams.map(dateParam => (
                <ParamItem key={dateParam.paramId} name={dateParam.paramName}>
                  <ParamValue param={dateParam} />
                </ParamItem>
              ))}
            </div>
          );
        }

        return (
          <ParamItem
            key={`${param.paramId}-${index}`}
            name={param.paramName}
            isWide>
            <ParamValue param={param} />
          </ParamItem>
        );
      })}
    </>
  );
};

export const OperationDetailsContent = ({
  operation,
  onClose,
  onStatusChange,
  className,
}: Props) => {
  const { t } = useTranslation();
  const { assetsIconsData } = useAssetsIcons();
  const { operationTypeIconsData } = useOperationTypesIcons();
  const {
    details,
    isLoading,
    error,
    statusIcon,
    isCancelable,
    isPrintable,
    isPdfLoading,
    downloadPdf,
    requestCancel,
    ConfirmationModal,
  } = useOperationDetailsState({
    operation,
    onStatusChange,
    onCanceled: onClose,
  });

  const actions =
    isPrintable || isCancelable ? (
      <>
        {isPrintable && (
          <ButtonSmall
            icon={DownloadIcon}
            loading={isPdfLoading}
            onClick={downloadPdf}>
            {t('historyPage.printOperation.download')}
          </ButtonSmall>
        )}
        {isCancelable && (
          <ButtonSmall icon={Close16Icon} onClick={requestCancel}>
            {t('historyPage.cancelOperation.button')}
          </ButtonSmall>
        )}
      </>
    ) : null;

  const statusCode = details?.status.paramStatus ?? operation.status;
  const typeIcon = operationTypeIconsData?.value.find(
    icon => icon.operationType === operation.operationType
  )?.icon;

  const renderBody = () => {
    if (error) return <ApiErrorText error={error} size="small" />;

    if (isLoading || !details) {
      return <Loader className="h-[12rem] text-secondary" />;
    }

    const [firstBlock, ...otherBlocks] = getVisibleBlocks(details);

    return (
      <div className="flex flex-col gap-[1.6rem]">
        <div className="flex flex-col gap-[1.6rem]">
          <ParamItem name={details.status.paramName}>
            <div className="flex items-center gap-[0.8rem]">
              {statusIcon && (
                <img
                  src={statusIcon}
                  alt=""
                  className="size-[1.6rem] shrink-0"
                />
              )}
              <p className="medium5 text-primary wrap-anywhere">
                {details.status.paramValue}
              </p>
            </div>
          </ParamItem>

          {firstBlock && <ParamsGroup params={firstBlock} />}
        </div>

        {otherBlocks.map((params, index) => (
          <Fragment key={index}>
            <div className="h-px shrink-0 bg-secondary-bg" />
            <div className="flex flex-col gap-[1.6rem]">
              <ParamsGroup params={params} />
            </div>
          </Fragment>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'relative flex min-h-0 flex-col overflow-hidden rounded-[1.6rem] border border-stroke-bg bg-background shadow-[0_0.4rem_0.8rem_0_rgba(0,0,0,0.04)]',
        className
      )}>
      <div className="min-h-0 overflow-y-auto p-[2.4rem]">
        <OperationDetailsHeader
          operation={operation}
          typeName={
            details && (getDisplayTitle(details) ?? details.operationType)
          }
          iconAssetCodes={details?.operationDisplay?.iconAssetCodes}
          assetsIcons={assetsIconsData?.value}
          typeIcon={typeIcon}
          isIconLoading={isLoading}
          actions={actions}
          amountHint={
            PROCESSING_STATUSES.includes(statusCode) ? (
              <OperationProcessingTimeHint />
            ) : undefined
          }
        />

        {renderBody()}
      </div>

      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="absolute right-[2.4rem] top-[2.4rem] z-10 flex size-[4.2rem] cursor-pointer items-center justify-center rounded-full border border-stroke-bg bg-background transition-colors hover:bg-secondary-bg">
        <CloseIcon className="size-[2.4rem] text-primary" />
      </button>

      {ConfirmationModal}
    </div>
  );
};
